import React, { useCallback, useEffect, useState } from 'react';
import { formatSessionExpiry, getUserTimezone } from '../../utils/timeUtils';
import authService from '../../services/authService';
import { warnLog } from '../../utils/logger';
import './SessionStatus.css';

const SessionStatus = () => {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dismissedTokenExp, setDismissedTokenExp] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const readSessionInfo = useCallback(() => {
    const token = authService.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTimestamp = new Date(payload.exp * 1000).toISOString();
      const accessExpiry = formatSessionExpiry(expiryTimestamp);
      const metadata = authService.getSessionMetadata();
      const persistence = metadata?.session_persistence || (metadata?.refresh_expires_at ? 'remember' : 'session');
      const sessionExpiry = metadata?.session_expires_at
        ? formatSessionExpiry(metadata.session_expires_at)
        : null;

      return {
        accessExpiry,
        sessionExpiry,
        persistence,
        tokenExp: payload.exp,
        timezone: getUserTimezone(),
      };
    } catch (error) {
      warnLog('Failed to parse session token:', error);
      return null;
    }
  }, []);

  const syncSessionState = useCallback(() => {
    const nextInfo = readSessionInfo();
    if (!nextInfo) {
      setSessionInfo(null);
      setIsVisible(false);
      return;
    }

    setSessionInfo(nextInfo);
    const isNearExpiry = nextInfo.accessExpiry.level === 'warning' || nextInfo.accessExpiry.level === 'error';
    const isDismissedForCurrentToken = dismissedTokenExp === nextInfo.tokenExp;
    setIsVisible(isNearExpiry && !isDismissedForCurrentToken);
  }, [dismissedTokenExp, readSessionInfo]);

  useEffect(() => {
    // Check immediately
    syncSessionState();

    // Check every minute
    const interval = setInterval(syncSessionState, 60000);

    return () => clearInterval(interval);
  }, [syncSessionState]);

  const handleRefreshSession = async () => {
    if (sessionInfo?.persistence !== 'remember') {
      return;
    }

    setIsRefreshing(true);
    try {
      const result = await authService.refreshToken();
      if (result.success) {
        setDismissedTokenExp(null);
        syncSessionState();
      }
    } catch (error) {
      warnLog('Failed to refresh session:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDismiss = () => {
    if (sessionInfo?.tokenExp) {
      setDismissedTokenExp(sessionInfo.tokenExp);
    }
    setIsVisible(false);
  };

  if (!isVisible || !sessionInfo) {
    return null;
  }

  return (
    <div className={`session-status session-status--${sessionInfo.accessExpiry.level}`}>
      <div className="session-status__content">
        <span className="session-status__icon" aria-hidden="true">!</span>
        <div className="session-status__text">
          <strong>{sessionInfo.accessExpiry.level === 'error' ? 'Session expiring now' : 'Session expiring soon'}</strong>
          <p>{sessionInfo.accessExpiry.text}</p>
          {sessionInfo.persistence === 'remember' && sessionInfo.sessionExpiry && (
            <p className="session-status__meta">Remembered session: {sessionInfo.sessionExpiry.text}</p>
          )}
          {sessionInfo.persistence !== 'remember' && (
            <p className="session-status__meta">You will need to sign in again after expiry.</p>
          )}
          <p className="session-status__timezone">Timezone: {sessionInfo.timezone}</p>
        </div>
        <div className="session-status__actions">
          {sessionInfo.persistence === 'remember' && (
            <button
              className="session-status__refresh"
              onClick={handleRefreshSession}
              title="Extend session"
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Extending...' : 'Extend'}
            </button>
          )}
          <button
            className="session-status__dismiss"
            onClick={handleDismiss}
            title="Dismiss this notice for current token"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionStatus;
